import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSessionState } from "@/shared/api";
import { restoreSession } from "../api/restore-session";
import { restorableTenantId } from "../api/tenant";
import { SessionProvider } from "./SessionProvider";

/**
 * Where a sign-in with no attempted location lands: the dashboard.
 *
 * It was `/practices`, which is the practice *configuration* screen — so a plain sign-in
 * opened on settings rather than on where the money stands.
 */
export const DEFAULT_SIGNED_IN_ROUTE = "/";
export const SIGN_IN_ROUTE = "/sign-in";

/**
 * doc 27 §6, the `401` row: "re-authenticate **preserving the current
 * location**".
 *
 * The location travels in the router's own `state`, not in the URL. A deep link
 * to a claim carries identifiers, and doc 23 §11a is absolute that no PHI ever
 * reaches a URL — including a `?next=` parameter, which lands in browser
 * history, proxy logs and referrer headers where our redaction layer does not
 * reach. Router state stays in memory.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { credentials } = useSessionState();
  const location = useLocation();
  /**
   * (H) One attempt, before deciding the visitor is signed out.
   *
   * The access token lives in memory, so a reload arrives here with no credential even when
   * a live session exists — which is what used to bounce every refresh and every pasted
   * deep link to the sign-in screen. `restoreSession` asks the server, authenticated by the
   * `HttpOnly` session cookie.
   *
   * `undefined` means "not asked yet" and is deliberately distinct from `false`: rendering
   * the redirect during that window would still lose the location, which is the whole thing
   * this guard exists to preserve.
   */
  const [restored, setRestored] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    // Nothing to do while a credential is held — and deliberately no state write here either.
    // Setting "restored: true" for a session that never needed restoring is a render's worth of
    // noise on every signed-in navigation, and the guard below already passes on the credential.
    if (credentials !== null) return undefined;

    let live = true;
    // Reset first, so a credential that goes away later — an expired token, a sign-out in
    // another tab — gets its own attempt rather than being judged by the previous one's answer.
    setRestored(undefined);
    void restoreSession(restorableTenantId()).then((ok) => {
      if (live) setRestored(ok);
    });
    return () => {
      live = false;
    };
  }, [credentials]);

  if (credentials === null && restored === undefined) {
    // Nothing is rendered rather than a spinner: the wait is one request against a warm
    // connection, and a flash of loading state on every navigation would be worse than a
    // beat of nothing.
    return null;
  }

  if (credentials === null) {
    return <Navigate to={SIGN_IN_ROUTE} replace state={{ from: location }} />;
  }

  // The session — identity, permissions, timezone — is loaded inside the guard
  // rather than around it, so an unauthenticated visitor never fires a `/me`
  // request that can only 401.
  return <SessionProvider>{children}</SessionProvider>;
}
